import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Label } from '../../../../../components/ui/label';
import { MAX_UPLOAD_FILE_SIZE_LABEL } from '../../../../../constants/fileUpload';
import { Input } from '../../../../../components/ui/input';
import { FileUpload } from '../../../../../components/ui/FileUpload';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { Plus } from 'lucide-react';
import type { DashboardProject } from '../../../types/project';
import { DocumentTag } from '../../../types';
import { AddMaterialCategory } from '../../../../../redux/Project/materialSaga';
import { getPreSignedUrlToUpload, bulkCreateUpdateConsumable } from '../../../../../redux/Project/registrySaga';
import { Notification as customNotification } from '../../../../../components/ui/custom-notification';
import AddNewItemDialog from '../../../../../components/ui/AddNewItemDialog';
import { getListMaterialCategory } from '../../../../../redux/Project/materialSaga';
import { SearchableSelect } from '../../../../../components/ui/SearchableSelect';
import { MultiSelect } from '../../../../../components/ui/multi-select';
import { AntdDate } from '../../../../../components/ui/antd-date';
import { refreshConsumablesData } from '../../../../../redux/Project/registryData/action';

interface AddNDEConsumableModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: () => void;
  project: DashboardProject;
  consumableToEdit?: any;
  consumableCategory?: string;
}

export const AddNDEConsumableModal: React.FC<AddNDEConsumableModalProps> = ({
  isOpen,
  onClose,
  onAdd,
  project,
  consumableToEdit,
  consumableCategory: initialConsumableCategory,
}) => {
  const dispatch = useDispatch();
  const isEditMode = !!consumableToEdit;
  const [consumableCategory, setConsumableCategory] = useState(initialConsumableCategory || 'NDE Consumables');

  // When modal opens with a specific category (e.g. Coating), use it
  useEffect(() => {
    if (isOpen && initialConsumableCategory) {
      setConsumableCategory(initialConsumableCategory);
    }
  }, [isOpen, initialConsumableCategory]);

  const [consumableType, setConsumableType] = useState('');
  const [type, setType] = useState('');
  const [lotNumber, setLotNumber] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [selectedManufacturer, setSelectedManufacturer] = useState<string[]>([]);
  const [expDate, setExpDate] = useState<string>('');
  const [materialId, setMaterialId] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [materialDocuments, setMaterialDocuments] = useState<File[]>([]);
  const [selectedTags, setSelectedTags] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [documentTags, setDocumentTags] = useState<DocumentTag[]>([]);
  const [manufacturers, setManufacturers] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [isAddingManufacturer, setIsAddingManufacturer] = useState(false);
  const [isAddingLocation, setIsAddingLocation] = useState(false);
  const [isAddingType, setIsAddingType] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Load document tags, manufacturers, and locations on mount
  useEffect(() => {
    const loadTags = async () => {
      try {
        const tags = await getListMaterialCategory();
        if (tags && tags.length > 0) {
          const consumableTags = tags.filter((tag: any) => tag.tags_type === 'EQUIPMENT_AND_CONSUMABLE_DOCUMENT_TAGS');
          setDocumentTags(consumableTags);

          const manufacturerTags = tags.filter((tag: any) =>
            tag.tags_type === 'MANUFACTURER' ||
            tag.tags_type === 'MANUFACTURER_TAGS' ||
            tag.tags_type?.toLowerCase().includes('manufacturer')
          );
          setManufacturers(manufacturerTags);

          const locationTags = tags.filter((tag: any) => tag.tags_type === 'MATERIAL_LOCATION_TAGS');
          setLocations(locationTags);

          const typeTags = tags.filter((tag: any) =>
            tag.tags_type === 'CONSUMABLE_TYPE_TAGS' ||
            tag.tags_type === 'NDE_CONSUMABLE_TYPE_TAGS' ||
            tag.tags_type === 'COATING_CONSUMABLE_TYPE_TAGS' ||
            tag.tags_type?.toLowerCase().includes('consumable_type')
          );
          setTypes(typeTags);
        }
      } catch (error) {
        console.error('Failed to load tags:', error);
      }
    };

    loadTags();
  }, []);

  // When modal opens, fetch type tags by category so NDE and Coating types are loaded
  useEffect(() => {
    if (!isOpen) return;
    const loadTypesByCategory = async () => {
      try {
        const typesToFetch = [
          'CONSUMABLE_TYPE_TAGS',
          'NDE_CONSUMABLE_TYPE_TAGS',
          'COATING_CONSUMABLE_TYPE_TAGS',
        ];
        const results = await Promise.all(
          typesToFetch.map((tagsType) => getListMaterialCategory(tagsType))
        );
        const combined = (results.flat().filter(Boolean) as any[]).filter(
          (tag: any, index: number, self: any[]) =>
            self.findIndex((t) => t.tags_id === tag.tags_id) === index
        );
        if (combined.length > 0) {
          setTypes((prev) => {
            const byId = new Map(prev.map((t: any) => [t.tags_id, t]));
            combined.forEach((t: any) => byId.set(t.tags_id, t));
            return Array.from(byId.values());
          });
        }
      } catch (err) {
        console.error('Failed to fetch types by category:', err);
      }
    };
    loadTypesByCategory();
  }, [isOpen]);

  // Convert manufacturers to options for MultiSelect
  const manufacturerOptions = useMemo(() =>
    manufacturers.map(manufacturer => ({
      value: manufacturer.tags_id?.toString() || manufacturer.manufacturerId?.toString() || '',
      label: manufacturer.tags_name || manufacturer.manufacturerName || ''
    })).filter(opt => opt.value && opt.label), [manufacturers]
  );

  // Convert locations to options for SearchableSelect
  const locationOptions = useMemo(() =>
    locations.map(location => ({
      id: location.tags_id?.toString() || '',
      name: location.tags_name || ''
    })).filter(opt => opt.id && opt.name), [locations]
  );

  // Convert types to options for SearchableSelect
  const typeOptions = useMemo(() =>
    types.map(type => ({
      id: type.tags_id?.toString() || '',
      name: type.tags_name || ''
    })).filter(opt => opt.id && opt.name), [types]
  );

  // Convert document tags to options for the MultiSelect component
  const tagOptions = useMemo(() =>
    documentTags.map(tag => ({
      value: tag.tags_id.toString(),
      label: tag.tags_name
    })), [documentTags]
  );

  // Initialize state when dialog opens
  useEffect(() => {
    if (isOpen) {
      if (consumableToEdit) {
        // Edit mode - populate with existing data
        setConsumableCategory(consumableToEdit.consumable_Category || consumableToEdit.consumable_category || 'Welding Consumables');
        setConsumableType(consumableToEdit.consumable_type || '');
        setType(consumableToEdit.type || '');
        setLotNumber(consumableToEdit.lot_number || '');
        setSerialNumber(consumableToEdit.serial_number || '');
        setMaterialId(consumableToEdit.material_id || '');
        setSelectedLocation(consumableToEdit.location || '');
        setNotes(consumableToEdit.notes || '');
        setExpDate(consumableToEdit.exp_date || consumableToEdit.expiry_date || '');

        // Handle manufacturer IDs
        const manufacturerIds = consumableToEdit.manufacturerIds || [];
        setSelectedManufacturer(Array.isArray(manufacturerIds) ? manufacturerIds.map((id: number) => id.toString()) : []);

        setMaterialDocuments([]);
        setSelectedTags([]);
      } else {
        // Add mode - reset to empty, keep category (e.g. Coating Consumables)
        setConsumableCategory(initialConsumableCategory || 'NDE Consumables');
        setConsumableType('');
        setType('');
        setLotNumber('');
        setSerialNumber('');
        setSelectedManufacturer([]);
        setExpDate('');
        setMaterialId('');
        setSelectedLocation('');
        setNotes('');
        setMaterialDocuments([]);
        setSelectedTags([]);
      }
    }
  }, [isOpen, consumableToEdit, initialConsumableCategory]);

  const handleMaterialFileSelect = (files: FileList | null) => {
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      setMaterialDocuments(prev => [...prev, ...fileArray]);
      // Initialize tags array for new files
      const newTags = Array(fileArray.length).fill([]);
      setSelectedTags(prev => [...prev, ...newTags]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setMaterialDocuments(prev => prev.filter((_, i) => i !== index));
    setSelectedTags(prev => prev.filter((_, i) => i !== index));
  };

  const handleTagsChange = (fileIndex: number, tags: string[]) => {
    const updatedTags = [...selectedTags];
    updatedTags[fileIndex] = tags;
    setSelectedTags(updatedTags);
  };

  const handleAddTag = async (tagName: string) => {
    if (tagName.trim()) {
      const existingTag = documentTags.find((tag: any) => tag.tags_name.toLowerCase() === tagName.trim().toLowerCase());
      if (existingTag) {
        customNotification.warning(`${tagName.trim()} tag already exists`);
        return;
      }

      const apiPayload = {
        tags_name: tagName.trim(),
        project_id: Number(project.project.projectId),
        tags_type: 'EQUIPMENT_AND_CONSUMABLE_DOCUMENT_TAGS',
        created_by: user.email || ''
      };
      const res = await AddMaterialCategory(apiPayload, 'Document Tag');

      if (res) {
        const updatedTags = [...documentTags, res];
        setDocumentTags(updatedTags);
        setIsAddingTag(false);
      }
    }
  };

  // Set manufacturer from names
  useEffect(() => {
    if (isOpen && consumableToEdit && manufacturerOptions.length > 0) {
      if (consumableToEdit.manufacturer && manufacturerOptions.length > 0) {
        const manufacturerNames = consumableToEdit.manufacturer.split(',').map((name: string) => name.trim());
        const matchingIds = manufacturerNames
          .map((name: string) => {
            const option = manufacturerOptions.find(opt =>
              opt.label.toLowerCase() === name.toLowerCase()
            );
            return option?.value;
          })
          .filter(Boolean) as string[];
        setSelectedManufacturer(matchingIds);
      }
    }
  }, [isOpen, consumableToEdit, manufacturerOptions]);

  const handleClose = () => {
    setConsumableCategory(initialConsumableCategory || 'NDE Consumables');
    setConsumableType('');
    setType('');
    setLotNumber('');
    setSerialNumber('');
    setSelectedManufacturer([]);
    setExpDate('');
    setMaterialId('');
    setSelectedLocation('');
    setNotes('');
    setMaterialDocuments([]);
    setSelectedTags([]);
    setIsUploading(false);
    onClose();
  };

  const getConsumableCategoryLabel = () => {
    return (initialConsumableCategory || consumableCategory || 'NDE Consumables').replace(/\s*Consumables\s*$/i, '');
  };

  // Get tags for consumable documents
  const getTags = () => {
    return documentTags.filter((tag: any) => tag.tags_type === 'EQUIPMENT_AND_CONSUMABLE_DOCUMENT_TAGS');
  };

  const handleAddManufacturer = async (manufacturerName: string) => {
    if (manufacturerName.trim()) {
      if (manufacturerOptions.find((opt: any) => opt.label.toLowerCase() === manufacturerName.trim().toLowerCase())) {
        customNotification.warning(`${manufacturerName.trim()} manufacturer already exists`);
        return;
      }

      const apiPayload = {
        tags_name: manufacturerName.trim(),
        project_id: Number(project.project.projectId),
        tags_type: 'MANUFACTURER',
        created_by: user.email || ''
      };
      const res = await AddMaterialCategory(apiPayload, 'Manufacturer');

      if (res) {
        const updatedManufacturers = [...manufacturers, res];
        setManufacturers(updatedManufacturers);
        setIsAddingManufacturer(false);
        // Set the newly added manufacturer as selected
        setSelectedManufacturer([res.tags_id?.toString() || '']);
      }
    }
  };

  const handleAddLocation = async (locationName: string) => {
    if (locationName.trim()) {
      if (locationOptions.find((opt: any) => opt.name.toLowerCase() === locationName.trim().toLowerCase())) {
        customNotification.warning(`${locationName.trim()} location already exists`);
        return;
      }

      const apiPayload = {
        tags_name: locationName.trim(),
        project_id: Number(project.project.projectId),
        tags_type: 'MATERIAL_LOCATION_TAGS',
        created_by: user.email || ''
      };
      const res = await AddMaterialCategory(apiPayload, 'Location');

      if (res) {
        const updatedLocations = [...locations, res];
        setLocations(updatedLocations);
        setIsAddingLocation(false);
        // Set the newly added location as selected
        setSelectedLocation(locationName.trim());
      }
    }
  };

  const getTypeTagsTypeByCategory = () => {
    const cat = consumableCategory || '';
    if (cat.toLowerCase().includes('coating')) return 'COATING_CONSUMABLE_TYPE_TAGS';
    if (cat.toLowerCase().includes('nde')) return 'NDE_CONSUMABLE_TYPE_TAGS';
    return 'CONSUMABLE_TYPE_TAGS';
  };

  const handleAddType = async (typeName: string) => {
    if (typeName.trim()) {
      if (typeOptions.find((opt: any) => opt.name.toLowerCase() === typeName.trim().toLowerCase())) {
        customNotification.warning(`${typeName.trim()} type already exists`);
        return;
      }

      const apiPayload = {
        tags_name: typeName.trim(),
        project_id: Number(project.project.projectId),
        tags_type: getTypeTagsTypeByCategory(),
        created_by: user.email || ''
      };
      const res = await AddMaterialCategory(apiPayload, 'Consumable Type');

      if (res) {
        const updatedTypes = [...types, res];
        setTypes(updatedTypes);
        setIsAddingType(false);
        // Set the newly added type as selected
        setType(typeName.trim());
      }
    }
  };

  const handleSubmit = async () => {
    // Only validate Consumable Type - all other fields are optional
    if (!consumableType?.trim()) {
      customNotification.warning('Please fill in Consumable Type');
      return;
    }

    setIsUploading(true);
    try {
      let documents: any[] = [];

      // If documents are uploaded, upload them to S3 first
      if (materialDocuments.length > 0) {
        const payload = {
          pipeId: 0,
          projectId: Number(project.project.projectId),
          projectTypeId: 1,
          type: 'CONSUMABLE'
        };

        const s3Response = await getPreSignedUrlToUpload(materialDocuments, payload, 1);

        if (s3Response && s3Response.length > 0) {
          documents = s3Response.map((file: any, index: number) => ({
            fileName: file.fileName,
            s3Key: file.s3Key,
            documentTags: selectedTags[index] || [],
            uploadedBy: user.email || '',
            docRef: index + 1
          }));
        }
      }

      const consumableItem: any = {
        project_id: Number(project.project.projectId),
        consumable_Category: consumableCategory || null,
        consumable_type: consumableType || null,
        // All other fields are optional - send value if exists, otherwise null/empty
        type: type?.trim() || null,
        lot_number: lotNumber?.trim() || null,
        serial_number: serialNumber?.trim() || null,
        manufacturer: selectedManufacturer.length > 0 ?
          selectedManufacturer.map(id => manufacturerOptions.find(opt => opt.value === id)?.label).filter(Boolean).join(', ') || null : null,
        manufacturerIds: [],
        material_id: materialId?.trim() || null,
        location: selectedLocation || null,
        notes: notes?.trim() || null,
        documents: documents.length > 0 ? documents : [],
        created_by: user.email || null
      };

      // exp_date: send value if exists, otherwise null
      consumableItem.exp_date = (expDate && expDate.trim() !== '') ? expDate : null;

      // If editing, include the id for update
      if (isEditMode && consumableToEdit?.id) {
        consumableItem.id = consumableToEdit.id;
      }

      const consumableData = {
        items: [consumableItem]
      };

      // Call API to create/update consumable
      const result = await bulkCreateUpdateConsumable(consumableData);

      if (result) {
        const label = getConsumableCategoryLabel();
        if (isEditMode) {
          customNotification.success(
            'Consumable updated successfully',
            `The ${label} consumable has been updated successfully.`
          );
        } else {
          customNotification.success(
            'Consumable added successfully',
            `The ${label} consumable has been added successfully.`
          );
        }
        // Refresh Redux store data to update the consumables list
        dispatch(refreshConsumablesData({ projectId: project.project.projectId }));
        onAdd();
        handleClose();
      }
    } catch (error) {
      customNotification.error(
        'Failed to add consumable',
        'There was an error adding the consumable. Please try again.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="w-[90vw] max-w-[1200px]">
          <DialogHeader className="flex flex-row items-center justify-between gap-3 pr-14">
            <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
              {isEditMode ? 'Edit' : 'Add'} {getConsumableCategoryLabel()} Consumable
            </DialogTitle>
            <Button
              onClick={handleSubmit}
              disabled={isUploading || !consumableType.trim()}
              size="sm"
              className="h-9 px-4 bg-hero-red text-white hover:bg-hero-red-60"
            >
              {isUploading ? 'Saving...' : isEditMode ? 'Update Consumable' : 'Add Consumable'}
            </Button>
          </DialogHeader>

          <div className="grid p-2 grid-cols-4 gap-4 min-w-0 max-h-[75vh] flex flex-col overflow-y-auto overflow-x-hidden">
            {/* Consumable Category */}
            {/* <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey" htmlFor="consumableCategory">Consumable Category</Label>
              <Input
                id="consumableCategory"
                value={consumableCategory}
                onChange={(e) => setConsumableCategory(e.target.value)}
                placeholder="Enter consumable category"
                className="w-full"
              />
            </div> */}

            {/* Consumable Type */}
            <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey" htmlFor="consumableType">Consumable Type <span className="text-hero-red">*</span></Label>
              <Input
                id="consumableType"
                value={consumableType}
                onChange={(e) => setConsumableType(e.target.value)}
                placeholder="Enter consumable type"
                className="w-full"
                required
              />
            </div>

            {/* Type */}
            <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey" htmlFor="type">Type</Label>
              <div className="relative mt-1">
                <SearchableSelect
                  value={type}
                  onValueChange={(value: string) => {
                    setType(value);
                  }}
                  placeholder="Select type (e.g., ER70S-6)"
                  searchPlaceholder="Search types..."
                  options={typeOptions}
                  onAddNew={() => setIsAddingType(true)}
                  addNewText="Add New Type"
                />
              </div>
            </div>

          
              {/* Lot Number */}
              <div className="space-y-2">
                <Label className="text-[12px] font-medium text-text-grey" htmlFor="lotNumber">Lot Number</Label>
                <Input
                  id="lotNumber"
                  value={lotNumber}
                  onChange={(e) => setLotNumber(e.target.value)}
                  placeholder="Enter lot number"
                  className="w-full"
                />
              </div>

              {/* Serial Number */}
              <div className="space-y-2">
                <Label className="text-[12px] font-medium text-text-grey" htmlFor="serialNumber">Serial Number</Label>
                <Input
                  id="serialNumber"
                  value={serialNumber}
                  onChange={(e) => setSerialNumber(e.target.value)}
                  placeholder="Enter serial number"
                  className="w-full"
                />
              </div>

            {/* Manufacturer */}
            <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey">Manufacturer</Label>
              <MultiSelect
                options={manufacturerOptions}
                selected={selectedManufacturer}
                onChange={(value: string[]) => {
                  setSelectedManufacturer(value);
                }}
                placeholder="Select manufacturer"
                isSearchable={true}
                className="w-full"
                showAddButton={true}
                addButtonText="Add New Manufacturer"
                onAddButtonClick={() => setIsAddingManufacturer(true)}
                maxHeight="200px"
              />
            </div>

         
              {/* Expiry Date */}
              <div className="space-y-2">
                <Label className="text-[12px] font-medium text-text-grey">Expiry Date</Label>
                <div className="flex items-center space-x-2">
                  <AntdDate
                    name="expDate"
                    value={expDate}
                    onChange={(value: string) => {
                      setExpDate(value);
                    }}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Material ID */}
              <div className="space-y-2">
                <Label className="text-[12px] font-medium text-text-grey" htmlFor="materialId">Material ID</Label>
                <Input
                  id="materialId"
                  value={materialId}
                  onChange={(e) => setMaterialId(e.target.value)}
                  placeholder="Enter material ID"
                  className="w-full"
                />
              </div>
            

            {/* Location */}
            <div className="space-y-2">
              <Label className="text-[12px] font-medium text-text-grey" htmlFor="location">Location</Label>
              <div className="relative mt-1">
                <SearchableSelect
                  value={selectedLocation}
                  onValueChange={(value: string) => {
                    setSelectedLocation(value);
                  }}
                  placeholder="Select location"
                  searchPlaceholder="Search locations..."
                  options={locationOptions}
                  onAddNew={() => setIsAddingLocation(true)}
                  addNewText="Add New Location"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2 col-span-4">
              <Label className="text-[12px] font-medium text-text-grey">Notes</Label>
              <textarea
                value={notes || ''}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full mt-2 p-3 border border-gray-300 rounded-md min-h-[60px] resize-none focus:outline-none focus:ring-1 focus:ring-blue-700 focus:"
                placeholder="Enter notes..."
              />
            </div>

            <div className="space-y-2 col-span-4">
              <Label className="text-[12px] font-medium text-text-grey">Upload Documents</Label>
              <p className="text-[12px] text-heritage-grey">
                Upload the equipment documents.
                <br />
                <span className="text-[11px] text-heritage-grey">
                  Allowed: PDF, Word (.doc, .docx), Excel (.xls, .xlsx), Text (.txt), Images (.jpg, .png, .gif)
                  <br />
                  Maximum file size: {MAX_UPLOAD_FILE_SIZE_LABEL} per file
                </span>
              </p>

              <FileUpload
                onFileSelect={handleMaterialFileSelect}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
                multiple={true}
                maxFiles={100}
                buttonText="Upload Documents"
                variant="outline"
                size="lg"
                onRemoveFile={handleRemoveFile}
                selectedFiles={materialDocuments}
                showFileList={materialDocuments.length > 0}
                className="w-full"
                showTags={true}
                optionsTags={tagOptions}
                selectedTags={selectedTags}
                showAddButton={true}
                addButtonText="Add New Tag"
                onAddButtonClick={() => setIsAddingTag(true)}
                onTagsChange={handleTagsChange}
                maxHeight=""
              />

              <div className="text-[11px] text-heritage-grey">
                Current files: {materialDocuments.length} file(s)
              </div>
            </div>
          </div>

        </DialogContent>
      </Dialog>

      {/* Add New Manufacturer Dialog */}
      <AddNewItemDialog
        open={isAddingManufacturer}
        onOpenChange={setIsAddingManufacturer}
        title="Add New Manufacturer"
        label="Manufacturer Name"
        placeholder="Enter manufacturer name"
        onAdd={handleAddManufacturer}
        buttonText="Add Manufacturer"
        cancelText="Cancel"
      />

      {/* Add New Location Dialog */}
      <AddNewItemDialog
        open={isAddingLocation}
        onOpenChange={setIsAddingLocation}
        title="Add New Location"
        label="Location Name"
        placeholder="Enter location name"
        onAdd={handleAddLocation}
        buttonText="Add Location"
        cancelText="Cancel"
      />

      {/* Add New Tag Dialog */}
      <AddNewItemDialog
        open={isAddingTag}
        onOpenChange={setIsAddingTag}
        title="Add New Tag"
        label="Tag Name"
        placeholder="Enter tag name"
        onAdd={handleAddTag}
        buttonText="Add Tag"
        cancelText="Cancel"
      />

      {/* Add New Type Dialog */}
      <AddNewItemDialog
        open={isAddingType}
        onOpenChange={setIsAddingType}
        title="Add New Type"
        label="Type Name"
        placeholder="Enter type name (e.g., ER70S-6)"
        onAdd={handleAddType}
        buttonText="Add Type"
        cancelText="Cancel"
      />
    </>
  );
};

